import { ReactNode } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const paddingVariants = cva(
  "",
  {
    variants: {
      padding: {
        default: "py-3 px-3 sm:py-4 sm:px-4 md:py-2 md:px-6",
        compact: "py-3 px-4 sm:py-4 sm:px-6 lg:py-2",
        spacious: "py-2 px-4 sm:py-8 sm:px-6 lg:py-12",
        none: "p-0",
      },
    },
    defaultVariants: {
      padding: "default",
    },
  }
);

const widthVariants = cva(
  "",
  {
    variants: {
      width: {
        default: "max-w-[1400px]",
        narrow: "max-w-[1000px]",
        wide: "max-w-[1600px]",
        full: "max-w-full",
      },
    },
    defaultVariants: {
      width: "default",
    },
  }
);

interface ContentLayoutProps {
  children: ReactNode;
  className?: string;
  padding?: "default" | "compact" | "spacious" | "none";
  width?: "default" | "narrow" | "wide" | "full";
}

/**
 * Global layout wrapper that provides consistent padding and spacing
 * across all pages. Ensures content is aligned with the VIN order status bar.
 * 
 * @param padding - Controls vertical and horizontal padding (default, compact, spacious, none)
 * @param width - Controls maximum content width (default: 1400px, narrow: 1000px, wide: 1600px, full: 100%)
 */
export function ContentLayout({ 
  children, 
  className = "",
  padding,
  width,
}: ContentLayoutProps) {
  return (
    <main className={cn("flex-1", paddingVariants({ padding }))}>
      <div className={cn(
        widthVariants({ width }),
        "mx-auto animate-fade-in",
        className
      )}>
        {children}
      </div>
    </main>
  );
}
